import type { Timestamp } from 'firebase/firestore'

// Import shared types from functions (source of truth)
import type {
  GifReference,
  GifPopularity,
  GifCoreData,
  CreateGifData,
  RetrievalSearch,
  RecommendGifsRequest,
  RecommendGifsResponse,
  RecommendationCandidate,
  ExplicitRating,
} from '../../../../functions/src/shared/types/gif'
import { isExplicitRating } from '../../../../functions/src/shared/types/gif'

// Re-export everything for external use
export type {
  ExplicitRating,
  GifReference,
  GifPopularity,
  GifCoreData,
  CreateGifData,
  RetrievalSearch,
  RecommendGifsRequest,
  RecommendGifsResponse,
  RecommendationCandidate,
}

export { isExplicitRating }

// CLIENT-SPECIFIC TYPES (not shared with functions)

// Raw document structure as stored in Firestore (with Timestamps).
//
// The frame-based display taxonomy (visualElements / emotionalProfile /
// useCases) was retired in the 2026-07-10 unified ingestion pipeline. Display +
// search now read `retrievalSearch` (denormalized on the gifs doc at approval).
// Those legacy fields are kept OPTIONAL here so the client tolerates
// pre-migration docs that still carry them (render retrievalSearch when present,
// fall back to the legacy field otherwise).
export interface GifDocumentData {
  // Basic metadata
  createdAt: Timestamp
  lastModified: Timestamp
  durationSeconds: number
  fileSizeBytes: number
  gifUrl: string

  // Core gif data
  reference: GifReference
  explicitRating: ExplicitRating

  // Denormalized retrieval prose (present once approved / post-migration).
  retrievalSearch?: RetrievalSearch
  /** First-approval timestamp; presence = published. */
  publishedAt?: string
  published?: boolean

  // Computed fields
  popularity: GifPopularity

  // Legacy pre-migration display fields — read defensively, never written.
  promptVersion?: string
  visualElements?: LegacyVisualElements
  emotionalProfile?: LegacyEmotionalProfile
  useCases?: string[]
}

/** Legacy display shape kept only for pre-migration tolerance. */
export interface LegacyVisualElements {
  description?: string
  setting?: string | null
  visualTags?: string[]
  text?: string | null
}

/** Legacy emotional shape kept only for pre-migration tolerance. */
export interface LegacyEmotionalProfile {
  coreEmotion?: string | null
  emotionalTags?: string[]
}

// Converted structure for use in the app (Timestamps converted to Dates)
export interface Gif {
  id: string

  // Basic metadata
  createdAt: Date
  lastModified: Date
  durationSeconds: number
  fileSizeBytes: number
  gifUrl: string

  // Core gif data
  reference: GifReference
  explicitRating: ExplicitRating

  // Denormalized retrieval prose (present once approved / post-migration).
  retrievalSearch?: RetrievalSearch
  publishedAt?: string
  published?: boolean

  // Computed fields
  popularity: GifPopularity

  // Legacy pre-migration display fields — read defensively for fallback.
  promptVersion?: string
  visualElements?: LegacyVisualElements
  emotionalProfile?: LegacyEmotionalProfile
  useCases?: string[]
}

// Type for gif document with metadata from Firestore
export interface GifWithMetadata extends Gif {
  exists: boolean
}

/**
 * The display title for a gif: first sentence of the retrieval narrative, with
 * a graceful fall back to the legacy display description (pre-migration) and
 * finally the reference source or id. Central so every display surface renders
 * the same thing.
 */
export function gifDisplayTitle(
  gif: Pick<Gif, 'retrievalSearch' | 'visualElements' | 'reference' | 'id'>
): string {
  const narrative = gif.retrievalSearch?.narrative?.trim()
  if (narrative) {
    const match = narrative.match(/^.*?[.!?](\s|$)/)
    return (match ? match[0] : narrative).trim()
  }
  const legacy = gif.visualElements?.description?.trim()
  if (legacy) return legacy
  return gif.reference?.source || gif.id
}

/** Display chips for a gif: breadthTags, falling back to legacy visual tags. */
export function gifDisplayChips(
  gif: Pick<Gif, 'retrievalSearch' | 'visualElements'>
): string[] {
  const breadth = gif.retrievalSearch?.breadthTags
  if (breadth && breadth.length > 0) return breadth
  return gif.visualElements?.visualTags ?? []
}

/** Scenario examples for a gif (empty when absent / pre-migration). */
export function gifScenarioExamples(
  gif: Pick<Gif, 'retrievalSearch'>
): string[] {
  return gif.retrievalSearch?.scenarioExamples ?? []
}
